/**
 * PTELTooltip - Componente Tooltip Accesible para PTEL
 * V04 - Bloque B (UI base)
 *
 * Características de accesibilidad (WCAG 2.1 AA / RD 1112/2018):
 * - Activación por hover Y focus (accesibilidad teclado)
 * - role="tooltip" + aria-describedby para lectores de pantalla
 * - Contraste WCAG ≥4.5:1 verificado
 * - Delay configurable para evitar parpadeo
 * - Escape para cerrar
 *
 * @see docs/INVESTIGACION_MULTIPERSPECTIVA_PTEL_20251210_0710.md
 * @see U03 - Tooltips contextuales en todos los campos
 *
 * INFDOC-2a (Luis, 3-nov-2026): colores del DS v2 en vez del `slate-900` de
 * Tailwind (azul marino #0f172a, ajeno a la paleta): fondo `bg.overlay`
 * («tooltips y capas flotantes»), borde `border.DEFAULT`, texto `fg.primary`;
 * la flecha, del mismo fondo. Contraste fg.primary sobre bg.overlay ≈ 16:1.
 */

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';
// Por ruta de fichero, no por el barril (HX-MOCK-FACTORY-BARREL-COUPLING)
import { colors } from '@/design-system/v2/design-tokens';

const ESTILO_CONTENIDO: React.CSSProperties = {
  background: colors.bg.overlay,
  color: colors.fg.primary,
  border: `1px solid ${colors.border.DEFAULT}`,
};

// =============================================================================
// TIPOS
// =============================================================================

/** Posiciones válidas para el tooltip */
export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

/** Props del componente PTELTooltip */
export interface PTELTooltipProps {
  /** Contenido del tooltip (texto o React nodes) */
  content: React.ReactNode;
  /** Elemento que activa el tooltip (trigger) */
  children: React.ReactNode;
  /** Posición del tooltip respecto al trigger */
  position?: TooltipPosition;
  /** Delay en ms antes de mostrar (evita parpadeo). Por defecto: 300ms */
  delayMs?: number;
  /** Delay en ms antes de ocultar (evita cierre accidental). Por defecto: 100ms */
  delayHideMs?: number;
  /** Offset en px desde el trigger. Por defecto: 6px */
  offset?: number;
  /** Clase CSS adicional para el contenido */
  className?: string;
  /** ID único para aria-describedby. Se genera automáticamente si no se proporciona */
  id?: string;
  /** Deshabilitar el tooltip */
  disabled?: boolean;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Valores por defecto optimizados para accesibilidad y UX */
export const TOOLTIP_DEFAULTS = {
  /** Delay para mostrar - evita parpadeo en movimientos rápidos */
  DELAY_MS: 300,
  /** Delay para ocultar - permite mover el cursor al tooltip */
  DELAY_HIDE_MS: 100,
  /** Distancia del trigger en pixels */
  OFFSET: 6,
  /** Posición por defecto */
  POSITION: 'top' as TooltipPosition,
} as const;

/**
 * Mapeo de posición PTEL a side de Radix
 * @internal
 */
const POSITION_TO_SIDE: Record<TooltipPosition, TooltipPrimitive.TooltipContentProps['side']> = {
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right',
};

// =============================================================================
// UTILIDADES
// =============================================================================

/**
 * Genera un ID único para aria-describedby
 * @internal
 */
let tooltipIdCounter = 0;
function generateTooltipId(): string {
  tooltipIdCounter += 1;
  return `ptel-tooltip-${tooltipIdCounter}`;
}

// =============================================================================
// COMPONENTE
// =============================================================================

/**
 * Tooltip accesible para PTEL
 *
 * @example
 * // Uso básico
 * <PTELTooltip content="Sistema de coordenadas UTM zona 30">
 *   <button>UTM30</button>
 * </PTELTooltip>
 *
 * @example
 * // Con posición y delay personalizados
 * <PTELTooltip
 *   content="Validación exitosa"
 *   position="right"
 *   delayMs={500}
 * >
 *   <StatusIcon />
 * </PTELTooltip>
 *
 * @example
 * // Con contenido enriquecido
 * <PTELTooltip
 *   content={
 *     <div>
 *       <strong>EPSG:25830</strong>
 *       <p>Sistema de referencia oficial de España</p>
 *     </div>
 *   }
 * >
 *   <InfoIcon />
 * </PTELTooltip>
 */
export function PTELTooltip({
  content,
  children,
  position = TOOLTIP_DEFAULTS.POSITION,
  delayMs = TOOLTIP_DEFAULTS.DELAY_MS,
  delayHideMs: _delayHideMs = TOOLTIP_DEFAULTS.DELAY_HIDE_MS,
  offset = TOOLTIP_DEFAULTS.OFFSET,
  className,
  id: providedId,
  disabled = false,
}: PTELTooltipProps): React.JSX.Element {
  // Generar ID único para aria-describedby
  const tooltipId = React.useMemo(
    () => providedId ?? generateTooltipId(),
    [providedId]
  );

  // Si está deshabilitado, solo renderizar children
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Provider delayDuration={delayMs}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild aria-describedby={tooltipId}>
          {children}
        </TooltipPrimitive.Trigger>

        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            id={tooltipId}
            role="tooltip"
            side={POSITION_TO_SIDE[position]}
            sideOffset={offset}
            className={cn(
              // Base: fondo oscuro con texto claro (contraste >4.5:1)
              'z-50 overflow-hidden rounded-md px-3 py-2',
              // Colores: DS v2 por `style` (ESTILO_CONTENIDO, INFDOC-2a)
              // Tipografía
              'text-sm leading-relaxed',
              // Sombra sutil para separación visual
              'shadow-md',
              // Ancho máximo para textos largos
              'max-w-xs',
              // Animaciones suaves
              'animate-in fade-in-0 zoom-in-95',
              'data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0',
              'data-[state=closed]:zoom-out-95',
              // Dirección de entrada según posición
              'data-[side=bottom]:slide-in-from-top-2',
              'data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2',
              'data-[side=top]:slide-in-from-bottom-2',
              // Clase personalizada
              className
            )}
            style={ESTILO_CONTENIDO}
            // Cerrar al hacer scroll para evitar tooltips flotantes
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            {content}
            
            {/* Flecha indicadora, del color del fondo */}
            <TooltipPrimitive.Arrow
              style={{ fill: colors.bg.overlay }}
              width={10}
              height={5}
            />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

// =============================================================================
// VARIANTES ESPECIALIZADAS
// =============================================================================

/** Props para tooltip informativo con icono */
export interface InfoTooltipProps extends Omit<PTELTooltipProps, 'children'> {
  /** Tamaño del icono en pixels */
  iconSize?: number;
}

/**
 * Tooltip informativo con icono de ayuda (?) incorporado
 * Útil para campos de formulario que necesitan explicación
 *
 * @example
 * <div className="flex items-center gap-2">
 *   <label>Coordenada X</label>
 *   <InfoTooltip content="Valor en metros desde el meridiano de referencia" />
 * </div>
 */
export function InfoTooltip({
  iconSize = 16,
  ...props
}: InfoTooltipProps): React.JSX.Element {
  return (
    <PTELTooltip {...props}>
      <button
        type="button"
        className={cn(
          'inline-flex items-center justify-center rounded-full',
          'text-slate-500 hover:text-slate-700',
          'focus:outline-none focus-visible:ring-2',
          'focus-visible:ring-blue-500 focus-visible:ring-offset-2',
          'transition-colors'
        )}
        style={{ width: iconSize, height: iconSize }}
        aria-label="Más información"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-full w-full"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </PTELTooltip>
  );
}

/**
 * Props para tooltip de error/advertencia
 */
export interface ValidationTooltipProps extends PTELTooltipProps {
  /** Tipo de validación que afecta el estilo */
  variant?: 'error' | 'warning' | 'success' | 'info';
}

/**
 * Tooltip para mensajes de validación con colores semánticos
 * Usa los tokens de V02 para consistencia visual
 *
 * @example
 * <ValidationTooltip
 *   variant="error"
 *   content="Las coordenadas están fuera del rango válido"
 * >
 *   <CoordinateField />
 * </ValidationTooltip>
 */
export function ValidationTooltip({
  variant = 'info',
  className,
  ...props
}: ValidationTooltipProps): React.JSX.Element {
  const variantClasses: Record<string, string> = {
    error: 'bg-red-600 text-white',
    warning: 'bg-amber-500 text-slate-900',
    success: 'bg-green-600 text-white',
    info: 'bg-slate-900 text-slate-50',
  };

  return (
    <PTELTooltip
      className={cn(variantClasses[variant], className)}
      {...props}
    />
  );
}

// Export por defecto para uso simplificado
export default PTELTooltip;
